import React, { useState, useRef, useCallback, useMemo } from 'react';
import {
  Box, Typography, Stack, IconButton, Chip,
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Collapse,
} from '@mui/material';
import KeyboardArrowUpIcon   from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon             from '@mui/icons-material/Close';
import EventNoteIcon         from '@mui/icons-material/EventNote';
import UnfoldMoreIcon        from '@mui/icons-material/UnfoldMore';
import ArrowUpwardIcon       from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon     from '@mui/icons-material/ArrowDownward';
import celfinetLogo from '../../../../assets/Cyient_Slate_Std_RGB.png';
import SiteGridRow from './SiteGridRow';

const toMs = (d) => {
  if (!d) return -Infinity;
  const str   = String(d).split('T')[0];
  const parts = str.split('-');
  if (parts.length === 3) {
    const ms = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2])).getTime();
    return isNaN(ms) ? -Infinity : ms;
  }
  const ms = new Date(d).getTime();
  return isNaN(ms) ? -Infinity : ms;
};

const STATUS_ORDER = { inprogress: 0, planned: 1, none: 2 };

const COLUMNS = [
  { key: 'status',    label: 'Status',  sort: (a, b) => (STATUS_ORDER[a._status] ?? 2) - (STATUS_ORDER[b._status] ?? 2) },
  { key: 'title',     label: 'Title',   sort: (a, b) => (a.title   || '').localeCompare(b.title   || '') },
  { key: 'feature',   label: 'Type', sort: (a, b) => (a.feature || '').localeCompare(b.feature || '') },
  { key: 'team',      label: 'Team',    sort: (a, b) => (a.team    || '').localeCompare(b.team    || '') },
  { key: 'owner',     label: 'Owner',   sort: (a, b) => (a.owner   || '').localeCompare(b.owner   || '') },
  { key: 'startDate', label: 'Start',   sort: (a, b) => toMs(a.startDate) - toMs(b.startDate) },
  { key: 'endDate',   label: 'End',     sort: (a, b) => toMs(a.endDate)   - toMs(b.endDate)   },
];

const MIN_HEIGHT     = 80;
const MAX_HEIGHT     = 520;
const DEFAULT_HEIGHT = 260;

const chipSx = (bg) => ({
  backgroundColor: `${bg}22`, color: bg, border: `1px solid ${bg}55`,
  height: 18, fontSize: 10, '& .MuiChip-label': { px: 0.8 },
});

const thSx = {
  backgroundColor: '#F8FAFC', color: '#64748B', fontWeight: 700,
  fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.8,
  borderBottom: '1px solid #E2E8F0', py: 0.8, px: 1.5,
};

const SiteGrid = ({ selectedSite, onClose, hideOld = false, onHideOldChange }) => {
  const [open, setOpen]             = useState(true);
  const [height, setHeight]         = useState(DEFAULT_HEIGHT);
  const [sortKey, setSortKey]       = useState('startDate');
  const [sortDir, setSortDir]       = useState('asc');
  const dragging                    = useRef(false);
  const startY                      = useRef(0);
  const startH                      = useRef(0);

  const activities = selectedSite?.activities || [];

  const today = useMemo(() => new Date().setHours(0,0,0,0), []);

  const filtered = useMemo(() => {
    if (hideOld) return activities;
    return activities.filter((act) => {
      if (act._status === 'closed') return false;
      if (act.endDate && new Date(act.endDate).setHours(0,0,0,0) < today) return false;
      return true;
    });
  }, [activities, hideOld, today]);

  const sorted = useMemo(() => {
    const col = COLUMNS.find((c) => c.key === sortKey);
    if (!col) return filtered;
    return [...filtered].sort((a, b) => {
      const cmp = col.sort(a, b);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const onMouseDown = useCallback((e) => {
    e.preventDefault();
    dragging.current = true;
    startY.current   = e.clientY;
    startH.current   = height;
    const onMove = (ev) => {
      if (!dragging.current) return;
      setHeight(Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, startH.current + (startY.current - ev.clientY))));
    };
    const onUp = () => {
      dragging.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup',   onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onUp);
  }, [height]);

  if (!selectedSite) return null;

  return (
    <Box sx={{ width: '100%', borderTop: '1px solid #E2E8F0', backgroundColor: '#fff', flexShrink: 0 }}>

      {/* Drag handle */}
      <Box onMouseDown={onMouseDown}
        sx={{ height: 6, cursor: 'row-resize', backgroundColor: 'transparent',
              '&:hover': { backgroundColor: '#E2E8F0' }, transition: 'background-color 0.15s',
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ width: 32, height: 2, borderRadius: 1, backgroundColor: '#CBD5E0' }} />
      </Box>

      {/* Header bar */}
      <Stack direction="row" alignItems="center" spacing={1}
        sx={{ px: 2, py: 0.8, backgroundColor: '#F8FAFC', borderBottom: open ? '1px solid #E2E8F0' : 'none', cursor: 'pointer' }}
        onClick={() => setOpen((v) => !v)}>
        <EventNoteIcon sx={{ color: '#1565C0', fontSize: 16 }} />
        <Typography variant="caption" fontWeight={700} sx={{ color: '#1565C0', textTransform: 'uppercase', letterSpacing: 1, fontSize: 11 }}>
          Activities
        </Typography>
        <Chip label={selectedSite.site}   size="small" sx={chipSx('#1565C0')} />
        <Chip label={selectedSite.tech}   size="small" sx={chipSx('#1B3A1B')} />
        <Chip label={selectedSite.vendor} size="small" sx={chipSx('#1B3A1B')} />
        {selectedSite.server && <Chip label={selectedSite.server} size="small" sx={chipSx('#2A1B3A')} />}
        {selectedSite.tac    && <Chip label={`TAC: ${selectedSite.tac}`} size="small" sx={chipSx('#2A1B3A')} />}
        <Chip label={`${activities.length} activit${activities.length === 1 ? 'y' : 'ies'}`}
          size="small" sx={chipSx(activities.length > 0 ? '#FF6B35' : '#546E7A')} />

        {/* Active only toggle switch */}
        <Stack direction="row" alignItems="center" spacing={0.8}
          onClick={(e) => { e.stopPropagation(); onHideOldChange && onHideOldChange(!hideOld); }}
          sx={{ cursor: 'pointer', ml: 1, userSelect: 'none' }}>
          <Box sx={{
            width: 32, height: 16, borderRadius: 8, position: 'relative',
            backgroundColor: hideOld ? '#1565C0' : '#CBD5E0',
            border: hideOld ? '1px solid #1565C0' : '1px solid #94A3B8',
            transition: 'background-color 0.2s, border-color 0.2s',
            flexShrink: 0,
          }}>
            <Box sx={{
              position: 'absolute', top: 2, left: hideOld ? 17 : 2,
              width: 10, height: 10, borderRadius: '50%',
              backgroundColor: '#fff',
              transition: 'left 0.2s',
            }} />
          </Box>
          <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: hideOld ? '#1565C0' : '#94A3B8' }}>
            Show Closed / Overdue
          </Typography>
        </Stack>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
          sx={{ color: '#64748B', p: 0.4 }}>
          {open ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowUpIcon fontSize="small" />}
        </IconButton>
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onClose?.(); }}
          sx={{ color: '#94A3B8', p: 0.4, '&:hover': { color: '#EF4444' } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      {/* Table */}
      <Collapse in={open}>
        <Box sx={{ height, overflowY: 'auto',
        position: 'relative',
                   '&::-webkit-scrollbar': { width: 4 },
                   '&::-webkit-scrollbar-thumb': { background: '#CBD5E0', borderRadius: 2 } }}>
                    {/* --- MARCA D'ÁGUA --- */}
    <Box sx={{ 
      position: 'absolute', 
      top: '50%', 
      left: '50%', 
      transform: 'translate(-50%, -50%)', 
      pointerEvents: 'none', 
      zIndex: 0, 
      width: '40%',
      opacity: 0.04, // Um pouco mais suave para não atrapalhar a leitura das linhas densas
      display: 'flex',
      justifyContent: 'center'
    }}>
      <Box component="img" src={celfinetLogo} alt="" sx={{ width: '100%', maxWidth: 300 }} />
    </Box>
    {/* ------------------- */}
          {activities.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                No tracker activities found for this site
              </Typography>
            </Box>
          ) : (
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {COLUMNS.map((col) => {
                    const active = sortKey === col.key;
                    const Icon   = active ? (sortDir === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon) : UnfoldMoreIcon;
                    return (
                      <TableCell key={col.key} onClick={() => handleSort(col.key)}
                        sx={{ ...thSx, cursor: 'pointer', userSelect: 'none',
                              color: active ? '#1565C0' : '#64748B', '&:hover': { color: '#1565C0' } }}>
                        <Stack direction="row" alignItems="center" spacing={0.4}>
                          <span>{col.label}</span>
                          <Icon sx={{ fontSize: 12, opacity: active ? 1 : 0.4 }} />
                        </Stack>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map((act, i) => <SiteGridRow key={i} act={act} />)}
              </TableBody>
            </Table>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default SiteGrid;